import React, { useState, useEffect, useRef } from "react";
import { CircularProgress } from "@mui/material";
import CurrencyConverter from "@heartinz/currency_subunit_converter";
import { usePermission } from "../../../context/PermissionContext";
import { useShowAlert } from "../../../context/AlertContext";
import Messages from "../../../constants/Messages";
import { useContextApi } from "../../../context/ContextApi";
import { CheckIcon, LineDoubleIcon, XIcon } from "../../../constants/Icons";

const CloseBalanceModal = ({
  onClose,
  onCBalance,
  onBalanceData,
  onClosingData,
  isBtnLoading,
}) => {
  const [denominations, setDenominations] = useState({
    fivehundred: 0,
    twohundred: 0,
    onehundred: 0,
    fifty: 0,
    twenty: 0,
    ten: 0,
    five: 0,
    two: 0,
    one: 0,
    coins: 0,
  });
  const {useEscapeKey}=useContextApi();
  useEscapeKey(onClose);
  const [savedDenominations, setSavedDenominations] = useState({
    fivehundred: 0,
    twohundred: 0,
    onehundred: 0,
    fifty: 0,
    twenty: 0,
    ten: 0,
    five: 0,
    two: 0,
    one: 0,
    coins: 0,
  });

  const {permission,hasPermission}=usePermission();
  const showAlert = useShowAlert();
  const [errorMessage, setErrorMessage] = useState(null);
  const [focusedInput, setFocusedInput] = useState(null);
  const [savingDenomination, setSavingDenomination] = useState(null);
  const isNavigatingWithKeys = useRef(false);
  const inputRefs = useRef([]);
  const checkBtnRefs = useRef([]);
  const doneButtonRef = useRef(null);
  inputRefs.current = [];

  const denominationMap = {
    50000: "fivehundred",
    20000: "twohundred",
    10000: "onehundred",
    5000: "fifty",
    2000: "twenty",
    1000: "ten",
    500: "five",
    200: "two",
    100: "one",
    coins: "coins",
  };

  const denominationValues = {
    fivehundred: 500,
    twohundred: 200,
    onehundred: 100,
    fifty: 50,
    twenty: 20,
    ten: 10,
    five: 5,
    two: 2,
    one: 1,
    coins: 1,
  };

  useEffect(() => {
    if (
      onClosingData?.denominationData &&
      Array.isArray(onClosingData.denominationData)
    ) {
      const newDenominations = { ...denominations };
      const newSavedDenominations = { ...savedDenominations };

      onClosingData.denominationData.forEach(
        ({ denominationName, denominationValue, quantity }) => {
          const key =
            denominationName in newDenominations
              ? denominationName
              : denominationMap[denominationValue];

          if (key in newDenominations) {
            newDenominations[key] = quantity || 0;
            newSavedDenominations[key] = quantity || 0;
          }
        }
      );
      setDenominations(newDenominations);
      setSavedDenominations(newSavedDenominations);
    }
  }, [onClosingData]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      let isOutside = true;

      const allRefs = [
        ...Object.values(inputRefs.current),
        ...Object.values(checkBtnRefs.current),
        doneButtonRef.current,
      ];

      for (const ref of allRefs) {
        if (ref && ref.contains(event.target)) {
          isOutside = false;
          break;
        }
      }

      if (isOutside && focusedInput) {
        setDenominations((prev) => ({
          ...prev,
          [focusedInput]: savedDenominations[focusedInput],
        }));
        setFocusedInput(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [focusedInput, savedDenominations]);

  const calculateSums = () => {
    const sums = {
      fivehundredsum: (parseInt(denominations.fivehundred) || 0) * 500,
      twohundredsum: (parseInt(denominations.twohundred) || 0) * 200,
      onehundredsum: (parseInt(denominations.onehundred) || 0) * 100,
      fiftysum: (parseInt(denominations.fifty) || 0) * 50,
      twentysum: (parseInt(denominations.twenty) || 0) * 20,
      tensum: (parseInt(denominations.ten) || 0) * 10,
      fivesum: (parseInt(denominations.five) || 0) * 5,
      twosum: (parseInt(denominations.two) || 0) * 2,
      onesum: (parseInt(denominations.one) || 0) * 1,
      coins: parseInt(denominations.coins || 0),
    };

    const totalAmount =
      sums.fivehundredsum +
      sums.twohundredsum +
      sums.onehundredsum +
      sums.fiftysum +
      sums.twentysum +
      sums.tensum +
      sums.fivesum +
      sums.twosum +
      sums.onesum +
      sums.coins;

    return {
      fivehundredsum: sums.fivehundredsum.toFixed(2),
      twohundredsum: sums.twohundredsum.toFixed(2),
      onehundredsum: sums.onehundredsum.toFixed(2),
      fiftysum: sums.fiftysum.toFixed(2),
      twentysum: sums.twentysum.toFixed(2),
      tensum: sums.tensum.toFixed(2),
      fivesum: sums.fivesum.toFixed(2),
      twosum: sums.twosum.toFixed(2),
      onesum: sums.onesum.toFixed(2),
      coins: sums.coins.toFixed(2),
      totalAmount: totalAmount.toFixed(2),
    };
  };

  const sums = calculateSums();

  const handleFocus = (e) => {
    const newFocusedInput = e.target.name;

    // Don't revert previous input
    isNavigatingWithKeys.current = false; // reset the flag
    setFocusedInput(newFocusedInput);
    e.target.select();
  };

  const handleInputChange = (name, value) => {
    setFocusedInput(name);

    if (value === "") {
      setDenominations((prev) => ({ ...prev, [name]: "" }));
      setErrorMessage(null);
      return;
    }

    if (/^[0-9]+$/.test(value)) {
      setDenominations((prev) => ({ ...prev, [name]: parseInt(value) }));
      setErrorMessage(null);
    } else {
      setErrorMessage();
    }
  };

  const handleClose = () => {
    onClose();
  };

  const handleDone = async () => {
    if((hasPermission("BOXCASH","BOXCASH:addClosingBalance") || permission===undefined)){
    const latest = { ...denominations };
    if (focusedInput !== null) {
      const inputEl = inputRefs.current.find((el) => el?.name === focusedInput);
      if (inputEl) {
        const value = parseInt(inputEl.value || "0");
        latest[focusedInput] = isNaN(value) ? 0 : value;
      }
    }
    setSavedDenominations(latest);
    setDenominations(latest);
    const updatedTotalAmount = Object.entries(latest).reduce(
      (acc, [key, qty]) => {
        const value = key === "coins" ? 1 : denominationValues[key];
        return acc + (parseInt(qty) || 0) * value;
      },
      0
    );
    const denominationList = Object.entries(latest).map(([key, qty]) => ({
      denominationName: key,
      denominationValue:
        key === "coins"
          ? 100
          : CurrencyConverter.toSubunit(denominationValues[key] || 0, "INR"),
      quantity: isNaN(parseInt(qty)) ? 0 : parseInt(qty),
    }));
    const finalPayload = {
      totalAmount: CurrencyConverter.toSubunit(updatedTotalAmount, "INR"),
      denominations: denominationList,
    };
    await onCBalance(finalPayload);
    onClose();
  }else{
    showAlert("warning", Messages.AUTH.PERMISSION_REQUIRED);
  }
  };

  const handleSaveCash = async (denomination, quantity, name) => {
    if((hasPermission("BOXCASH","BOXCASH:addClosingBalance") || permission===undefined)){
    const parsedQuantity = parseInt(quantity);

    if (isNaN(parsedQuantity) || parsedQuantity < 0) {
      setErrorMessage("");
      return;
    }

    const prevSavedValue = savedDenominations[name] ?? 0;
    if (parsedQuantity === 0 && prevSavedValue === 0) {
      setFocusedInput(null);
      return;
    }
    if (parsedQuantity === prevSavedValue) {
      setFocusedInput(null);
      return;
    }
    setSavingDenomination(name);
    const updatedDenominations = {
      ...savedDenominations,
      [name]: parsedQuantity,
    };

    const updatedTotalAmount = Object.entries(updatedDenominations).reduce(
      (acc, [key, qty]) => {
        const value = key === "coins" ? 1 : denominationValues[key];
        return acc + (parseInt(qty) || 0) * value;
      },
      0
    );

    const totalAmountSubunit = CurrencyConverter.toSubunit(
      parseFloat(updatedTotalAmount || 0),
      "INR"
    );

    const denominationList = Object.entries(updatedDenominations).map(
      ([key, qty]) => ({
        denominationName: key,
        denominationValue:
          key === "coins"
            ? 100
            : CurrencyConverter.toSubunit(denominationValues[key] || 0, "INR"),
        quantity: isNaN(parseInt(qty)) ? 0 : parseInt(qty),
      })
    );

    const finalPayload = {
      totalAmount: totalAmountSubunit,
      denominations: denominationList,
    };

    await onCBalance(finalPayload);
    setSavedDenominations(updatedDenominations);
    setDenominations((prev) => ({
      ...prev,
      [name]: parsedQuantity,
    }));
    setSavingDenomination(null);
    setFocusedInput(null);
  }else{
    showAlert("warning", Messages.AUTH.PERMISSION_REQUIRED);
  }
  };

  const denominationDisplay = [
    { key: "fivehundred", label: "500", value: 500 },
    { key: "twohundred", label: "200", value: 200 },
    { key: "onehundred", label: "100", value: 100 },
    { key: "fifty", label: "50", value: 50 },
    { key: "twenty", label: "20", value: 20 },
    { key: "ten", label: "10", value: 10 },
    { key: "five", label: "5", value: 5 },
    { key: "two", label: "2", value: 2 },
    { key: "one", label: "1", value: 1 },
    { key: "coins", label: "Coins", value: 1 },
  ];

  const handleKeyNavigation = (e, key, index, label) => {
    const value = String(denominations[key] ?? "").trim();
    const isLastInput = index === denominationDisplay.length - 1;

    if (e.key === "Enter") {
      e.preventDefault();

      handleSaveCash(label === "Coins" ? "coins" : label, value, key);

      setTimeout(() => {
        if (isLastInput) {
          const doneBtn = document.getElementById("doneButton");
          doneBtn?.focus();
          // handleClose();
        } else {
          isNavigatingWithKeys.current = true; // 👈 Add this line
          const nextKey = denominationDisplay[index + 1]?.key;
          const nextInput = inputRefs.current[index + 1];
          if (nextInput) {
            nextInput.focus();
            setFocusedInput(nextKey);
          }
        }
      }, 0);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      isNavigatingWithKeys.current = true;

      const isLastInput = index === denominationDisplay.length - 1;

      if (isLastInput) {
        const doneBtn = document.getElementById("doneButton");
        doneBtn?.focus();
      } else {
        const nextKey = denominationDisplay[index + 1]?.key;
        const nextInput = inputRefs.current[index + 1];
        if (nextInput) {
          nextInput.focus();
          setFocusedInput(nextKey);
        }
      }
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      isNavigatingWithKeys.current = true; // 👈 Add this line
      const prevKey = denominationDisplay[index - 1]?.key;
      const prevInput = inputRefs.current[index - 1];
      if (prevInput) {
        prevInput.focus();
        setFocusedInput(prevKey);
      }
    }
  };

  useEffect(() => {
    const firstInput = inputRefs.current[0];
    if (firstInput) {
      firstInput.focus();
      firstInput.select();

      // ❌ DON'T set focusedInput here
      // setFocusedInput(denominationDisplay[0].key);
    }
  }, []);

  return (
    <div>
      <div className="modal-backdrop show"></div>
      <div
        className="modal show"
        tabIndex="-1"
        aria-modal="true"
        role="dialog"
        style={{ display: "block" }}
      >
        <div className="modal-dialog modal-dialog-centered modal-md">
          <div className="modal-content">
            <div className="modal-header">
              <h6 className="modal-title">Closing Balance</h6>
              <button
                type="button"
                onClick={handleClose}
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body p-0">
              <div className="card w-100 radius-10 mb-0 shadow-none p-0">
                <div className="card-body px-3">
                  {errorMessage && (
                    <div className="alert alert-danger p-2 text-center">
                      {errorMessage}
                    </div>
                  )}
                  <div className="table-block">
                    <table className="table border radius-10 table-sm mb-0 align-middle">
                      <thead className="table-light">
                        <tr>
                          <th className="text-center">Currency</th>
                          <th></th>
                          <th className="text-center">Quantity</th>
                          <th></th>
                          <th className="text-center">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {denominationDisplay.map(({ key, label }, index) => (
                          <tr key={key}>
                            <td className="text-center">{label}</td>
                            <td>
                              <XIcon iClass={"font-18"}/>
                            </td>
                            <td>
                              <div className="d-flex">
                                <input
                                  type="text"
                                  name={key}
                                  autoComplete="off"
                                  value={denominations[key] ?? ""}
                                  ref={(el) => (inputRefs.current[index] = el)}
                                  onChange={(e) =>
                                    handleInputChange(key, e.target.value)
                                  }
                                  onFocus={(e) => {
                                    const name = e.target.name;
                                    setFocusedInput(name);
                                    e.target.select();
                                  }}
                                  onKeyDown={(e) =>
                                    handleKeyNavigation(e, key, index, label)
                                  }
                                  className="text-center form-control"
                                />

                                {focusedInput === key &&
                                  String(denominations[key] ?? "") !==
                                    String(savedDenominations[key] ?? "") && (
                                    <button
                                      ref={(el) =>
                                        (checkBtnRefs.current[index] = el)
                                      }
                                      className="btn btn-sm btn-primary ms-2"
                                      onClick={() => {
                                        const inputValue = denominations[key];
                                        const denomKey =
                                          label === "Coins" ? "coins" : key;
                                        handleSaveCash(
                                          denomKey,
                                          inputValue,
                                          key
                                        );
                                        setTimeout(() => {
                                          inputRefs.current[index + 1]?.focus();
                                        }, 50);
                                      }}
                                      disabled={
                                        isBtnLoading ||
                                        savingDenomination === key
                                      }
                                    >
                                      {savingDenomination === key ? (
                                        <CircularProgress
                                          size={16}
                                          color="inherit"
                                        />
                                      ) : (
                                        <CheckIcon/>
                                      )}
                                    </button>
                                  )}
                              </div>
                            </td>
                            <td>
                              <LineDoubleIcon iClass={"font-18"}/>
                            </td>
                            <td>
                              <input
                                type="text"
                                className="text-center form-control"
                                readOnly
                                disabled
                                name={`${key}sum`}
                                value={sums[`${key}sum`] || sums[key]}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="table-light">
                        <tr>
                          <th></th>
                          <th></th>
                          <th className="text-center">Total Amount</th>
                          <th></th>
                          <th className="text-center">{sums.totalAmount}</th>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer d-block">
              <button
                onClick={handleClose}
                type="button"
                data-bs-dismiss="modal"
                className="btn btn-outline-secondary text-left"
              >
                <XIcon/> Close
              </button>
              <button
                id="doneButton"
                ref={doneButtonRef}
                tabIndex={-1}
                onClick={handleDone}
                className="btn btn-primary shadow-none btn-sm text-right"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleDone();
                  }
                }}
              >
                <CheckIcon iClass={"font-15"}/> Done
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CloseBalanceModal;
